// @deprecated F12.1 — Legacy site-wide Header. Replaced by HeaderV4
//             (`components/header-v4/HeaderV4.jsx`). Still imported by the
//             authenticated branch of `SidebarLayout` and used as the
//             fallback when `REACT_APP_HEADER_V4_ENABLED=false`.
//             Do not add new features here — add them in HeaderV4.
import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Coins, ChevronDown, User as UserIcon, LogOut, Sparkles, CreditCard, Database, Bot } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import NotificationBell from "@/components/NotificationBell";
import CommunityNotificationBell from "@/components/community_v2/NotificationBell";
import CartWidget from "@/components/marketplace/CartWidget";
import PrimaryNavV3 from "@/components/header-v3/PrimaryNav/PrimaryNavV3";  // Legacy · non più montato (header-to-sidebar hotfix)

const PLAN_COLOR = {
    free: "border-[var(--color-border)] text-text-secondary",
    starter: "border-[var(--color-secondary)]/40 bg-[var(--color-secondary)]/10 text-[var(--color-secondary)]",
    pro: "border-[var(--color-primary)] bg-[var(--color-primary)]/15 text-[var(--color-primary)]",
    business: "border-[var(--color-success)]/40 bg-[var(--color-success)]/10 text-[var(--color-success)]",
    enterprise: "border-[var(--color-alert)]/40 bg-[var(--color-alert)]/10 text-[var(--color-alert)]",
};

export default function Header() {
    const { user, logout, plan } = useAuth();
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [communityFlags, setCommunityFlags] = useState({
        COMMUNITY_ENGINE_ENABLED: false,
        COMMUNITY_DM_ENABLED: false,
        COMMUNITY_GROUPS_ENABLED: false,
        AI_COMMUNITY_ENABLED: false,
    });
    const ref = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    useEffect(() => {
        // Phase 19 · lightweight flag check for the V2 community bell.
        if (!user) return;
        const API = process.env.REACT_APP_BACKEND_URL;
        fetch(`${API}/api/community/flags`, { credentials: "include" })
            .then((r) => r.ok ? r.json() : null)
            .then((d) => { if (d) setCommunityFlags(d); })
            .catch(() => {});
    }, [user]);

    if (!user) return null;
    const planId = user.plan_id || "free";
    const planLabel = plan?.name || planId.charAt(0).toUpperCase() + planId.slice(1);

    return (
        <header
            data-testid="app-header"
            className="sticky top-0 z-30 flex items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-bg)]/85 backdrop-blur-xl px-6 py-3"
        >
            <div className="flex items-center gap-6">
                {/* F8.5 · brand/logo left-slot (kept minimal — no debug badges). */}
                <Link
                    to="/dashboard"
                    data-testid="header-logo-home"
                    className="inline-flex items-center gap-2 text-text-main hover:opacity-80 transition"
                    aria-label="Global Lead Suite home"
                >
                    <span className="w-6 h-6 rounded-md bg-white flex items-center justify-center text-black font-bold text-[11px]">
                        G
                    </span>
                    <span className="text-sm font-semibold tracking-tight">Global Lead Suite</span>
                </Link>
                {/* Hotfix header-to-sidebar · PrimaryNavV3 rimosso dall'header dashboard.
                    I gruppi mega-menu sono ora renderizzati nella sidebar sinistra
                    (components/Sidebar.jsx) come sezioni collassabili. */}
            </div>

            <div className="flex items-center gap-3">
                <LanguageSwitcher compact />
                <CartWidget />
                <NotificationBell />
                {/* F8.5 · CommunityNotificationBell rimosso — consolidato in un solo bell (NotificationBell copre già tutti gli eventi). */}
                {/* Phase 23a — AI Community entry point (only if flag is ON) · V12 achromatic */}
                {communityFlags.AI_COMMUNITY_ENABLED && (
                    <Link
                        to="/ai"
                        data-testid="header-ai-community-link"
                        className="inline-flex items-center gap-1.5 rounded-full border border-neutral-700 bg-transparent px-3 py-1.5 text-xs uppercase tracking-widest text-neutral-200 hover:border-neutral-400 hover:text-white transition"
                    >
                        <Bot className="h-3 w-3" />
                        AI Community
                    </Link>
                )}
                <Link
                    to="/billing"
                    data-testid="header-plan-badge"
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs uppercase tracking-widest hover:opacity-90 transition ${PLAN_COLOR[planId] || PLAN_COLOR.free}`}
                >
                    <Sparkles className="h-3 w-3" />
                    {planLabel}
                </Link>

                <Link
                    to="/credits"
                    data-testid="header-credits"
                    className="flex items-center gap-2 rounded-full border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm hover:border-neutral-500 transition-colors"
                >
                    <Coins className="h-4 w-4 text-neutral-400" />
                    <span
                        data-testid="header-credits-value"
                        className="tabular-nums font-medium text-neutral-100"
                    >
                        {user.credits ?? 0}
                    </span>
                    <span className="text-neutral-500 text-xs">{t("nav.credits").toLowerCase()}</span>
                </Link>

                <div className="relative" ref={ref}>
                    <button
                        data-testid="header-user-menu"
                        onClick={() => setOpen((o) => !o)}
                        className="flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-2 py-1.5 hover:border-[var(--color-primary)]/50 transition-colors"
                    >
                        <span className="h-7 w-7 rounded-full bg-[var(--color-panel-ai)] grid place-items-center overflow-hidden">
                            {user.picture ? (
                                <img
                                    src={user.picture}
                                    alt=""
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <span className="text-[11px] font-semibold">
                                    {(user.name || user.email)
                                        .slice(0, 2)
                                        .toUpperCase()}
                                </span>
                            )}
                        </span>
                        <span className="text-sm hidden sm:block max-w-[140px] truncate">
                            {user.name}
                        </span>
                        <ChevronDown className="h-3.5 w-3.5 text-text-secondary" />
                    </button>

                    {open && (
                        <div
                            data-testid="header-user-dropdown"
                            className="absolute right-0 mt-2 w-56 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-lg overflow-hidden animate-fade-in-up"
                        >
                            <div className="px-4 py-3 border-b border-[var(--color-border)]">
                                <p className="text-sm font-medium truncate">
                                    {user.name}
                                </p>
                                <p className="text-xs text-text-secondary truncate">
                                    {user.email}
                                </p>
                                <p className="text-[10px] mt-1 uppercase tracking-widest text-[var(--color-secondary)]">
                                    {user.role}
                                </p>
                            </div>
                            <Link
                                to="/profile"
                                onClick={() => setOpen(false)}
                                data-testid="header-menu-profile"
                                className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[var(--color-panel-ai)] transition-colors"
                            >
                                <UserIcon className="h-4 w-4" />
                                {t("nav.profile")}
                            </Link>
                            <Link
                                to="/billing"
                                onClick={() => setOpen(false)}
                                data-testid="header-menu-billing"
                                className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[var(--color-panel-ai)] transition-colors"
                            >
                                <CreditCard className="h-4 w-4" />
                                {t("nav.billing")}
                            </Link>
                            <Link
                                to="/settings/data"
                                onClick={() => setOpen(false)}
                                data-testid="header-menu-settings-data"
                                className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[var(--color-panel-ai)] transition-colors"
                            >
                                <Database className="h-4 w-4" />
                                Data & Privacy
                            </Link>
                            <button
                                onClick={logout}
                                data-testid="header-menu-logout"
                                className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-[var(--color-alert)] hover:bg-[var(--color-alert)]/10 transition-colors"
                            >
                                <LogOut className="h-4 w-4" />
                                {t("nav.logout")}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}
